import { useState } from "react";
import { useTranslation } from "react-i18next";
import useUsers from "~/api/swr/useUsers";
import { UserStatus } from "~/config/types";
import { Button } from "~/atoms/button";
import { toast } from "react-toastify";
import MoreTooltip from "../molecules/MoreTooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import { Checkbox } from "~/atoms/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "~/atoms/dialog";
import { addReaders } from "~/api/v2/requests";
import { useReaderReport } from "~/api/v2/useReaderReport";
import { type IdAndTitle } from "~/api/types";
import { CenterSpinner } from "~/atoms/spinner";

interface Props {
  documentVersionId: number;
  category: IdAndTitle;
}

const InviteReadersModal = ({ documentVersionId, category }: Props) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  const {
    data: report,
    mutate: mutateReport,
    isLoading: isLoadingReport,
  } = useReaderReport(documentVersionId);

  const {
    data: users,
    mutate: mutateUsers,
    isLoading: isLoadingUsers,
  } = useUsers({
    status: [UserStatus.active],
  });

  const invitedReaderIds = report?.readers.map((r) => r.userId);
  const augmentedUsers = users?.map((user) => ({
    ...user,
    isSelected: selectedUserIds.includes(user.id),
    isReader: invitedReaderIds?.includes(user.id) == true,
  }));

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setSelectedUserIds([]);
      void mutateReport();
      void mutateUsers();
    }

    setDialogOpen(open);
  };

  const handleToggleUser = (userId: number, isSelected: boolean) => {
    const newSelectedUserids = isSelected
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId];

    setSelectedUserIds(newSelectedUserids);
  };

  const selectAllFromCategory = () => {
    const newSelectedUserIds = augmentedUsers
      ?.filter(
        (user) =>
          user.categories?.some((d) => d.id === category.id) && !user.isReader,
      )
      .map((user) => user.id);

    if (newSelectedUserIds) {
      setSelectedUserIds(newSelectedUserIds);
    }
  };

  const selectAllUsers = () => {
    const newSelectedUserIds = augmentedUsers
      ?.filter((user) => user.isReader === false)
      .map((user) => user.id);

    if (newSelectedUserIds) {
      setSelectedUserIds(newSelectedUserIds);
    }
  };

  const unselectAllUsers = () => {
    setSelectedUserIds([]);
  };

  const sendInvitation = async () => {
    if (selectedUserIds.length === 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      await addReaders(documentVersionId, selectedUserIds);
      void mutateReport();
      toast.success(t(($) => $.toasts.success.readersInvited));
      setDialogOpen(false);
    } catch {
      toast.error(t(($) => $.commons.workflow.failedToAddReaders));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="default">{t(($) => $.modal.inviteUsers.title)}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-250">
        <DialogHeader>
          <DialogTitle>{t(($) => $.modal.inviteUsers.title)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.modal.inviteUsers.subText)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex max-w-min gap-2">
            <Button onClick={selectAllFromCategory} variant="secondary">
              {`${t(($) => $.commons.buttons.selectAllFrom)} ${category.title}`}
            </Button>
            <Button variant="secondary" onClick={selectAllUsers}>
              {t(($) => $.commons.buttons.selectAll)}
            </Button>
            <Button variant="secondary" onClick={unselectAllUsers}>
              {t(($) => $.commons.buttons.unselectAll)}
            </Button>
            <div className="flex gap-3"></div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead />
                <TableHead>{t(($) => $.modal.inviteUsers.name)}</TableHead>
                <TableHead>{t(($) => $.commons.role)}</TableHead>
                <TableHead>{t(($) => $.commons.document.category)}</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {(isLoadingReport || isLoadingUsers) && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center">
                    <CenterSpinner />
                  </TableCell>
                </TableRow>
              )}

              {augmentedUsers?.map((user) => (
                <TableRow
                  key={user.id}
                  className={user.isReader ? "" : "cursor-pointer"}
                  onClick={() => handleToggleUser(user.id, user.isSelected)}
                >
                  <TableCell>
                    <div className="flex h-full flex-1 items-center">
                      <Checkbox
                        disabled={user.isReader}
                        checked={user.isReader || user.isSelected}
                        onClick={(event) => event.stopPropagation()}
                        onCheckedChange={() =>
                          handleToggleUser(user.id, user.isSelected)
                        }
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.firstName} {user.lastName}
                  </TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span>{user.categories[0]?.title ?? "-"}</span>
                      <MoreTooltip data={user.categories.map((d) => d.title)} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <DialogFooter>
            <Button
              onClick={sendInvitation}
              disabled={isSubmitting || selectedUserIds.length === 0}
            >
              {t(($) => $.commons.buttons.sentInvitations)}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteReadersModal;
